import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';

import { PriceText } from '~/components/Price/PriceText';

import styles from './PriceSection.module.scss';

export type PriceSectionSize = 'l' | 'm' | 's';

export interface PriceSectionProps {
  /**
   * Current price to display
   */
  currentPrice: string;

  /**
   * Old price (shown when there's a discount)
   */
  oldPrice?: string;

  /**
   * Discount percentage (shown when there's a discount)
   */
  discount?: string;

  /**
   * Size variant (m, s)
   */
  size: PriceSectionSize;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Hide price (show only button)
   */
  hidePrice?: boolean;

  /**
   * Optional action rendered in the reserved trailing column (e.g. add-to-cart control)
   */
  action?: ReactNode;
}

/**
 * AddToCartButton - Component for initial product add to cart
 * Shows price + icon button, or just icon button if hidePrice is true
 */
const PriceSection = observer(
  ({
    currentPrice,
    oldPrice,
    discount,
    size,
    className,
    hidePrice = false,
    action,
  }: PriceSectionProps) => {
    const hasDiscount = Boolean(oldPrice && oldPrice !== currentPrice);
    const shouldShowPrice = size === 'l' && !hidePrice;

    return (
      <div
        className={classes([
          styles.container,
          styles[`container_${size}`],
          className,
        ])}
      >
        {shouldShowPrice && (
          <div className={styles.priceColumn}>
            <span
              className={classes([
                styles.currentPrice,
                styles[`currentPrice_${size}`],
                hasDiscount && styles.currentPrice_discounted,
              ])}
            >
              <PriceText>{currentPrice}</PriceText>
            </span>
            {hasDiscount && oldPrice && (
              <div className={styles.discountContainer}>
                <span
                  className={classes([
                    styles.oldPrice,
                    styles[`oldPrice_${size}`],
                  ])}
                >
                  <PriceText>{oldPrice}</PriceText>
                </span>
                {discount && (
                  <span className={styles.discountBadge}>{discount}</span>
                )}
              </div>
            )}
          </div>
        )}
        {action && <div className={styles.action}>{action}</div>}
      </div>
    );
  },
);

export default PriceSection;
